/**
 * FlowLandersOffersPanel
 * For Media Devoted | Created By Ciro Autiero
 *
 * Right column: Landers + Offers (list chrome matches Paths).
 */

import { FC, useMemo } from 'react';

import { useFlowOverlaySort } from '@/features/flows/detail/overlay-sort';
import { usePreviewMode } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { FlowOverlayMetricLabels } from '@/features/flows/detail/components/common/FlowOverlayMetricLabels';

import FlowWeightedItemRow from './FlowWeightedItemRow';
import {
  splitByActiveWeight,
  WeightedActiveInactiveSections,
} from './flow-landers-offers.helpers';

import type { FlowWeightedItemRowItem } from './flow-weighted-item.types';

type Metrics = Record<string, number>;

interface FlowLandersOffersPanelProps {
  selectedPathName?: string;
  selectedPathId: string;
  selectedLanderId?: string;
  selectedLanderName?: string;
  onSelectLander?: (landerId: string) => void;
  landers: FlowWeightedItemRowItem[];
  offers: FlowWeightedItemRowItem[];
  expandedLanderId: string | null;
  expandedOfferId: string | null;
  onToggleExpandLander: (landerId: string) => void;
  onToggleExpandOffer: (offerId: string) => void;
  onToggleLanderActive?: (landerId: string, active: boolean) => void;
  onToggleOfferActive?: (offerId: string, active: boolean) => void;
  landerMetricsById?: Record<string, Metrics>;
  offerMetricsById?: Record<string, Metrics>;
  /** Per-path maps (Binom): pathId → itemId → metrics. Preferred over the flat
   * maps so a lander/offer shared across paths shows its per-path value. */
  landerMetricsByPathId?: Record<string, Record<string, Metrics>>;
  offerMetricsByPathId?: Record<string, Record<string, Metrics>>;
  /** pathId → landerId → offerId → metrics: an offer's stats WITHIN a lander. */
  offerMetricsByPathLanderId?: Record<
    string,
    Record<string, Record<string, Metrics>>
  >;
  metricsRangeLabel?: string;
  isMutating?: boolean;
}

export const FlowLandersOffersPanel: FC<FlowLandersOffersPanelProps> = ({
  selectedPathName,
  selectedPathId,
  selectedLanderId,
  selectedLanderName,
  onSelectLander,
  landers,
  offers,
  expandedLanderId,
  expandedOfferId,
  onToggleExpandLander,
  onToggleExpandOffer,
  onToggleLanderActive,
  onToggleOfferActive,
  landerMetricsById,
  offerMetricsById,
  landerMetricsByPathId,
  offerMetricsByPathId,
  offerMetricsByPathLanderId,
  metricsRangeLabel,
  isMutating = false,
}) => {
  const overlaySort = useFlowOverlaySort();
  const previewMode = usePreviewMode();
  // Prefer the per-path value (a shared item differs per path); fall back to the
  // flat campaign-wide map when no per-path entry exists (e.g. Voluum).
  const landerMetrics = (id: string): Metrics | undefined =>
    landerMetricsByPathId?.[selectedPathId]?.[id] ?? landerMetricsById?.[id];
  // Offers prefer per-(path,lander) (the offer's stats within the selected
  // lander), then per-path, then the flat map.
  const offerMetrics = (id: string): Metrics | undefined =>
    (selectedLanderId
      ? offerMetricsByPathLanderId?.[selectedPathId]?.[selectedLanderId]?.[id]
      : undefined) ??
    offerMetricsByPathId?.[selectedPathId]?.[id] ??
    offerMetricsById?.[id];
  const sortedLanders = useMemo(() => {
    if (!overlaySort) return landers;
    return overlaySort.sortItems(landers, 'landers', selectedPathId);
  }, [landers, overlaySort, selectedPathId]);
  const sortedOffers = useMemo(() => {
    if (!overlaySort) return offers;
    return overlaySort.sortItems(offers, 'offers', selectedPathId);
  }, [offers, overlaySort, selectedPathId]);
  const landerSplit = useMemo(() => splitByActiveWeight(sortedLanders), [sortedLanders]);
  const offerSplit = useMemo(() => splitByActiveWeight(sortedOffers), [sortedOffers]);

  const landerTitleSuffix = selectedPathName ? ` — ${selectedPathName}` : '';
  const offerTitleSuffix = selectedLanderName ? ` — ${selectedLanderName}` : '';

  if (!selectedPathId) {
    return (
      <div className="col-span-12 px-3 py-2 text-sm text-muted-foreground sm:px-4 lg:col-span-8">
        Select a path to view its landers and offers.
      </div>
    );
  }

  return (
    <div className="col-span-12 flex min-h-0 flex-col gap-3 py-1.5 pl-2 pr-1 lg:col-span-8 lg:pl-3 lg:pr-1.5">
      <section className="min-h-0 space-y-3">
        <h3 className="text-base font-semibold leading-none tracking-tight">
          Landers{landerTitleSuffix}
        </h3>
        <WeightedActiveInactiveSections
          split={landerSplit}
          activeHeader={previewMode ? <FlowOverlayMetricLabels variant="item" /> : null}
          activeEmpty="No active landers."
          inactiveEmpty="No inactive landers."
          activeMaxHeightClassName="max-h-[min(42vh,32rem)]"
          inactiveMaxHeightClassName="max-h-[min(34vh,24rem)]"
          countKind="landers"
          renderRow={(i) => (
            <FlowWeightedItemRow
              key={i.id}
              kind="lander"
              item={i}
              pathId={selectedPathId}
              selected={selectedLanderId === i.id}
              expanded={expandedLanderId === i.id}
              onToggleExpand={() => onToggleExpandLander(i.id)}
              onSelect={() => onSelectLander?.(i.id)}
              onToggleActive={onToggleLanderActive && ((active) => onToggleLanderActive(i.id, active))}
              metrics={landerMetrics(i.id)}
              metricsRangeLabel={metricsRangeLabel}
              isMutating={isMutating}
            />
          )}
        />
      </section>

      <section className="min-h-0 space-y-3 border-t border-border pt-3">
        <h3 className="text-base font-semibold leading-none tracking-tight">
          Offers{offerTitleSuffix}
        </h3>
        <WeightedActiveInactiveSections
          split={offerSplit}
          activeHeader={previewMode ? <FlowOverlayMetricLabels variant="item" /> : null}
          activeEmpty="No active offers."
          inactiveEmpty="No inactive offers."
          activeMaxHeightClassName="max-h-[min(42vh,32rem)]"
          inactiveMaxHeightClassName="max-h-[min(34vh,24rem)]"
          countKind="offers"
          renderRow={(i) => (
            <FlowWeightedItemRow
              key={i.id}
              kind="offer"
              item={i}
              pathId={selectedPathId}
              landerId={selectedLanderId}
              expanded={expandedOfferId === i.id}
              onToggleExpand={() => onToggleExpandOffer(i.id)}
              onToggleActive={onToggleOfferActive && ((active) => onToggleOfferActive(i.id, active))}
              metrics={offerMetrics(i.id)}
              metricsRangeLabel={metricsRangeLabel}
              isMutating={isMutating}
            />
          )}
        />
      </section>
    </div>
  );
};

export default FlowLandersOffersPanel;
